import { useEffect, useState } from "react";
import TablaUsuariosVista from "../vista/tablaUsuariosVista";
import type { IUsuario } from "../../../modelos/Usuario/entidades/IUsuario";
import { getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { columnasTablaUsuario } from "../vista/constantesTablaUsuarios";
import { estadoInicialFiltroUsuario, type IEntidadesFiltroUsuario, type IUsuariosCVProps } from "../interfaces";
import React from "react";
import ModeloUsuario from "../../../modelos/Usuario";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { RegionalModelo } from "../../../modelos/Regionales";
import { UbicacionModelo } from "../../../modelos/Ubicacion";
import { ZonaModelo } from "../../../modelos/Zonas";
import type { IFiltroUsuario } from "../../../modelos/Usuario/entidades/IFiltroUsuario";

const UsuariosCV: React.FunctionComponent<IUsuariosCVProps> = () => {
  const modeloUsuario = new ModeloUsuario();
  const ubicacionModelo = new UbicacionModelo();
  const modeloRegional = new RegionalModelo();
  const modeloZona = new ZonaModelo();

  const { mostrarNotificacion } = useNotificaciones();
  const { manejarCargando } = useCargando();
  const [listaUsuarios, setListaUsuarios] = useState<IUsuario[]>([]);
  const [habilitarFiltros, setHabilitarFiltros] = useState(false);
  const [filtro, setFiltro] = useState<IFiltroUsuario>(estadoInicialFiltroUsuario);
  const PAGE_SIZE = 15;
  const [pageIndex, setPageIndex] = useState(0);
  const [entidadesFiltro, setEntidadesFiltro] = useState<IEntidadesFiltroUsuario>({
    Paises: [],
    Ciudades: [],
    CiudadesFiltro: [],
    Regionales: [],
    RegionalesFiltro: [],
    Zonas: []
  });

  useEffect(() => {
    consultarUsuarios(filtro);
    consultarEntidadesFiltro();
  }, [])

  const consultarEntidadesFiltro = async () => {
    try {
      manejarCargando(true);
      const [paises, ciudades, regionales, zonas] = await Promise.all([
        ubicacionModelo.obtenerPaises(),
        ubicacionModelo.obtenerCiudades(),
        modeloRegional.obtenerListaRegionales(),
        modeloZona.obtenerListaZonas()
      ]);
      setEntidadesFiltro({
        Paises: paises.Data,
        Ciudades: ciudades.Data,
        CiudadesFiltro: ciudades.Data,
        Regionales: regionales.Data,
        RegionalesFiltro: regionales.Data,
        Zonas: zonas.Data
      });
    }
    finally {
      manejarCargando(false);
    }
  }

  const consultarUsuarios = async (filtro: IFiltroUsuario) => {
    try {
      manejarCargando(true);
      const usuarios = await modeloUsuario.obtenerListaUsuarios(filtro);
      setListaUsuarios(usuarios.Data);
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar usuarios',
        'No fue posible consultar los usuarios. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const limpiarFiltros = () => {
    setFiltro(estadoInicialFiltroUsuario);
    consultarUsuarios(estadoInicialFiltroUsuario);
  }

  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroUsuario = (campo: keyof IFiltroUsuario, valor: IFiltroUsuario[keyof IFiltroUsuario]) => {
    setFiltro((prevEstado) => {
      let nuevoEstado = { ...prevEstado, [campo]: valor };
      if (campo === 'PaisId') {
        if (valor) {
          nuevoEstado.CiudadId = undefined;
          nuevoEstado.RegionalId = undefined;
          setEntidadesFiltro((prevEntidades) => ({
            ...prevEntidades,
            RegionalesFiltro: entidadesFiltro.Regionales.filter(r => r.PaisId === Number(valor)),
            CiudadesFiltro: entidadesFiltro.Ciudades.filter(c => c.PaisId === Number(valor))
          }));

        } else {
          setEntidadesFiltro((prevEntidades) => ({
            ...prevEntidades,
            RegionalesFiltro: entidadesFiltro.Regionales,
            CiudadesFiltro: entidadesFiltro.Ciudades
          }));
        }
      }
      return nuevoEstado;
    });
  }

  const tabla = useReactTable({
    data: listaUsuarios,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: PAGE_SIZE,
      },
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function'
        ? updater({ pageIndex, pageSize: PAGE_SIZE })
        : updater;
      setPageIndex(newPagination.pageIndex);
    },
  });

  const columns = columnasTablaUsuario({
    tabla
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TablaUsuariosVista
        tabla={tabla}
        consultarUsuarios={consultarUsuarios}
        filtro={filtro}
        manejarCambioFiltroUsuario={manejarCambioFiltroUsuario}
        limpiarFiltros={limpiarFiltros}
        entidadesFiltro={entidadesFiltro}
        habilitarFiltros={habilitarFiltros}
        manejarHabilitarFiltros={manejarHabilitarFiltros}
      />
    </div>
  )
}

export default React.memo(UsuariosCV);
